import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Tag, Alert, Empty, Spin, Card,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import apiClient from '../../utils/axios';
import LifecycleEditor from './LifecycleEditor';

function MiniFunnel({ stages, metrics }) {
  if (!stages || stages.length === 0 || !metrics) return null;

  const sorted = [...stages].sort((a, b) => a.order - b.order);
  const funnels = metrics.funnels || {};
  const maxCount = sorted.reduce((max, s) => {
    const count = funnels[s.key]?.count ?? 0;
    return Math.max(max, count);
  }, 0);

  if (maxCount === 0) {
    return (
      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', padding: '4px 0' }}>
        No funnel data yet
      </div>
    );
  }

  const labelWidth = Math.max(...sorted.map(s => s.label.length)) * 6.5 + 4;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {sorted.map((stage) => {
        const count = funnels[stage.key]?.count ?? 0;
        const widthPct = maxCount > 0 ? Math.max((count / maxCount) * 100, 12) : 12;

        return (
          <div key={stage.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              fontSize: 11,
              color: 'var(--text-secondary)',
              whiteSpace: 'nowrap',
              width: labelWidth,
              flexShrink: 0,
              textAlign: 'right',
            }}>
              {stage.label}
            </span>
            <div style={{
              width: `${widthPct}%`,
              minWidth: 28,
              height: 18,
              background: 'rgba(22,119,255,0.15)',
              border: '1px solid rgba(22,119,255,0.25)',
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-primary)' }}>
                {count.toLocaleString()}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function LifecycleListPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lifecycles, setLifecycles] = useState([]);
  const [funnelMap, setFunnelMap] = useState({});
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingLifecycle, setEditingLifecycle] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    apiClient.get('/iam/me').then(res => {
      const u = res.data;
      setIsAdmin(u.role === 'admin');
    }).catch(() => setIsAdmin(false));
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/lifecycles');
      const data = response.data;
      setLifecycles(data);

      const results = await Promise.all(
        data.map(lc =>
          apiClient.get(`/lifecycles/${lc.key}/funnel`, { params: { period: '30d' } })
            .then(r => [lc.key, r.data])
            .catch(() => [lc.key, null])
        )
      );
      setFunnelMap(Object.fromEntries(results));
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load lifecycles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingLifecycle(null);
    setEditorOpen(true);
  };

  const openEdit = (lifecycle, e) => {
    e.stopPropagation();
    setEditingLifecycle(lifecycle);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingLifecycle(null);
  };

  return (
    <div style={{
      background: 'var(--bg-primary)',
      borderRadius: 12,
      border: '1px solid var(--border-color)',
      padding: '1.5rem',
      gridColumn: '1 / -1',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 600 }}>
            Contact Lifecycles
          </h3>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
            {isAdmin ? 'Manage contact lifecycle definitions and stage progressions' : 'Contact lifecycle definitions and stage progressions'}
          </p>
        </div>
        {isAdmin && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New Lifecycle
            </Button>
          </div>
        )}
      </div>

      {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}

      <Spin spinning={loading}>
        {!loading && lifecycles.length === 0 ? (
          <Empty description="No lifecycles yet" style={{ padding: '2rem 0' }} />
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {lifecycles.map((lifecycle) => (
              <Card
                key={lifecycle.id}
                hoverable
                style={{
                  width: 380,
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 10,
                  cursor: 'pointer',
                }}
                styles={{ body: { padding: '1rem' } }}
                onClick={() => navigate(`/lifecycles/${lifecycle.key}`)}
              >
                {/* Card header row */}
                <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      marginBottom: 2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {lifecycle.name}
                    </div>
                    <div style={{
                      fontFamily: 'monospace',
                      fontSize: 12,
                      color: 'var(--text-muted)',
                      marginBottom: 4,
                    }}>
                      {lifecycle.key}
                    </div>
                  </div>
                  <div style={{ marginLeft: 8, flexShrink: 0, display: 'flex', gap: 6, alignItems: 'center' }}>
                    {lifecycle.is_active
                      ? <Tag color="green">Active</Tag>
                      : <Tag>Inactive</Tag>
                    }
                    {isAdmin && (
                      <Button
                        size="small"
                        type="text"
                        style={{ color: 'var(--text-muted)', fontSize: 12 }}
                        onClick={(e) => openEdit(lifecycle, e)}
                      >
                        Edit
                      </Button>
                    )}
                  </div>
                </div>

                {/* Description */}
                {lifecycle.description && (
                  <div style={{
                    fontSize: 13,
                    color: 'var(--text-secondary)',
                    marginBottom: 10,
                    lineHeight: 1.4,
                  }}>
                    {lifecycle.description}
                  </div>
                )}

                {/* Mini funnel preview */}
                <div style={{ marginBottom: 10 }}>
                  <MiniFunnel stages={lifecycle.stages} metrics={funnelMap[lifecycle.key]} />
                </div>

                {/* Stage count */}
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {lifecycle.stages ? lifecycle.stages.length : 0} stage{lifecycle.stages?.length !== 1 ? 's' : ''}
                </div>
              </Card>
            ))}
          </div>
        )}
      </Spin>

      {isAdmin && (
        <LifecycleEditor
          open={editorOpen}
          lifecycle={editingLifecycle}
          onClose={closeEditor}
          onSaved={load}
        />
      )}
    </div>
  );
}
